import { useState } from "react";
import { Plus, ChevronDown, ChevronRight } from "lucide-react";
import toast from "react-hot-toast";
import {
  usePayrollRuns,
  useApprovePayrollRun,
  useMarkPayrollRunPaid,
  useDeletePayrollRun,
  PAYROLL_RUN_STATUS_LABEL,
  PayrollRunStatus,
  payrollRunTotal,
} from "@/api/payroll";
import { useAuthStore } from "@/stores/authStore";
import { getApiErrorMessage } from "@/api/client";
import CreatePayrollRunModal from "./CreatePayrollRunModal";

const STATUS_STYLE: Record<PayrollRunStatus, string> = {
  BORRADOR: "bg-gray-100 text-gray-600",
  APROBADA: "bg-blue-100 text-blue-700",
  PAGADA: "bg-green-100 text-green-700",
};

const currency = (v: number) =>
  new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(v);

export default function PayrollRunsPage() {
  const [status, setStatus] = useState<PayrollRunStatus | "">("");
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { data, isLoading } = usePayrollRuns({ status: status || undefined, pageSize: 50 });
  const approveRun = useApprovePayrollRun();
  const markPaid = useMarkPayrollRunPaid();
  const deleteRun = useDeletePayrollRun();

  const runs = data?.data ?? [];

  const handleApprove = async (id: string) => {
    try {
      await approveRun.mutateAsync(id);
      toast.success("Corrida aprobada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleMarkPaid = async (id: string) => {
    try {
      await markPaid.mutateAsync(id);
      toast.success("Corrida marcada como pagada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("¿Eliminar esta corrida en borrador?")) return;
    try {
      await deleteRun.mutateAsync(id);
      toast.success("Corrida eliminada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Corridas de nómina</h1>
          <p className="text-sm text-gray-500">Liquidación por periodo: borrador → aprobada → pagada.</p>
        </div>
        {hasPermission("payroll.create") && (
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} className="mr-1.5" /> Nueva corrida
          </button>
        )}
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <select className="input-field w-auto" value={status} onChange={(e) => setStatus(e.target.value as PayrollRunStatus | "")}>
          <option value="">Todos los estados</option>
          {(Object.keys(PAYROLL_RUN_STATUS_LABEL) as PayrollRunStatus[]).map((s) => (
            <option key={s} value={s}>
              {PAYROLL_RUN_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </div>

      <div className="card overflow-hidden">
        {isLoading && <p className="p-6 text-center text-sm text-gray-400">Cargando...</p>}
        {!isLoading && runs.length === 0 && <p className="p-10 text-center text-sm text-gray-400">No hay corridas de nómina registradas.</p>}
        {runs.map((run) => (
          <div key={run.id} className="border-b border-gray-100 last:border-b-0">
            <div className="flex items-center justify-between px-4 py-3">
              <button type="button" className="flex items-center gap-2 text-left" onClick={() => setExpanded((e) => (e === run.id ? null : run.id))}>
                {expanded === run.id ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Periodo {run.periodLabel} <span className="text-gray-400">({run.startDate} a {run.endDate})</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    {run.lines.length} empleados · Total neto {currency(payrollRunTotal(run))}
                  </p>
                </div>
              </button>
              <div className="flex items-center gap-3">
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[run.status]}`}>
                  {PAYROLL_RUN_STATUS_LABEL[run.status]}
                </span>
                {run.status === "BORRADOR" && hasPermission("payroll.update") && (
                  <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => handleApprove(run.id)}>
                    Aprobar
                  </button>
                )}
                {run.status === "APROBADA" && hasPermission("payroll.update") && (
                  <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => handleMarkPaid(run.id)}>
                    Marcar pagada
                  </button>
                )}
                {run.status === "BORRADOR" && hasPermission("payroll.delete") && (
                  <button type="button" className="text-xs font-medium text-red-500 hover:text-red-700" onClick={() => handleDelete(run.id)}>
                    Eliminar
                  </button>
                )}
              </div>
            </div>
            {expanded === run.id && (
              <table className="min-w-full divide-y divide-gray-100 bg-gray-50 text-xs">
                <thead>
                  <tr className="text-gray-500">
                    <th className="px-4 py-2 text-left font-medium">Empleado</th>
                    <th className="px-4 py-2 text-left font-medium">Salario base</th>
                    <th className="px-4 py-2 text-left font-medium">Bonificaciones</th>
                    <th className="px-4 py-2 text-left font-medium">Salud (4%)</th>
                    <th className="px-4 py-2 text-left font-medium">Pensión (4%)</th>
                    <th className="px-4 py-2 text-left font-medium">Neto a pagar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {run.lines.map((line) => (
                    <tr key={line.id}>
                      <td className="px-4 py-2 text-gray-800">{line.employee?.fullName || "—"}</td>
                      <td className="px-4 py-2 text-gray-600">{currency(Number(line.baseSalary))}</td>
                      <td className="px-4 py-2 text-gray-600">{currency(Number(line.bonuses))}</td>
                      <td className="px-4 py-2 text-gray-600">{currency(Number(line.healthDeduction))}</td>
                      <td className="px-4 py-2 text-gray-600">{currency(Number(line.pensionDeduction))}</td>
                      <td className="px-4 py-2 font-medium text-gray-900">{currency(Number(line.netPay))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>

      {showCreate && <CreatePayrollRunModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
